import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchNotifications, markNotificationRead } from "@/lib/api";
import { loadedPermissionDecision, useLoadedPermission } from "@/hooks/use-permission";
import { useAuthStore } from "@/stores/auth-store";
import type { Notification, NotificationKind } from "@/types";

// POST /v1/notifications/{id}/read requires this backend scope and mapped permission.
// Both single and bulk actions use this declaration for rendering and dispatch.
export const NOTIFICATION_WRITE_ACTION = { permission: "notifications-prefs:full", scope: "notifications:write" } as const;

function notificationWriteAllowed() {
  return loadedPermissionDecision(useAuthStore.getState().experience, NOTIFICATION_WRITE_ACTION.permission, NOTIFICATION_WRITE_ACTION.scope).allowed;
}

export interface DashboardNotification {
  id: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

interface UseNotificationsResult {
  notifications: DashboardNotification[];
  /** Notification-center rows for the topbar bell and the `/notifications` page. */
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  source: "api";
  refresh: () => void;
  canMarkRead: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * The one notification feed the shell observes.
 *
 * It is `null` until a `NotificationsProvider` mounts, which is deliberate:
 * with no provider every consumer owns its own feed (the behaviour the hook
 * had before the bell and the page shared state, and what the hook-level unit
 * tests still exercise), and with a provider the topbar bell, the `/home` tile
 * and the `/notifications` page read and mutate the *same* rows — so marking a
 * row read on the page moves the bell's unread badge (review fix 2, ALL-5825 W3).
 */
export const NotificationsContext = createContext<UseNotificationsResult | null>(null);

function mapApiNotification(raw: Record<string, unknown>): DashboardNotification {
  const id = String(raw.id ?? "");
  const title = String(raw.title ?? raw.event ?? raw.eventType ?? "Notification");
  const body = String(raw.body ?? raw.message ?? raw.detail ?? "");
  const read = Boolean(raw.read);
  const createdAt = String(raw.createdAt ?? raw.created_at ?? "");
  return { id, title, body, read, createdAt };
}

/** Gateway event `type` → the row's icon/colour kind. Unknown types stay undefined. */
function kindFromEventType(type: string): NotificationKind | undefined {
  const prefix = type.split(".")[0];
  switch (prefix) {
    case "approval":
      return "approval";
    case "transfer":
      return "transfer";
    case "wallet":
      return "wallet";
    case "deployment":
      return "deployment";
    case "security":
      return "security";
    case "system":
      return "system";
    default:
      return undefined;
  }
}

/**
 * In-app deep link for a row, derived only from the subject the gateway sent.
 * Only the two subject types the inbox actually emits deep-link today; anything
 * else activates in place (marks read) rather than guessing a route.
 */
function hrefFromEvent(type: string, subjectRef: string): string | undefined {
  if (!subjectRef) return undefined;
  if (type.startsWith("approval.")) return `/approvals/${encodeURIComponent(subjectRef)}`;
  if (type.startsWith("transfer.")) return `/transfers/${encodeURIComponent(subjectRef)}`;
  return undefined;
}

/**
 * A row title when the gateway sent none: `approval.required` → `Approval
 * required`, so the row never falls back to a raw machine token while a
 * human-readable one can be derived from it without inventing anything.
 */
function humaniseEventType(type: string): string {
  const spaced = type
    .replace(/[._-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Relative age of a row from its own timestamp. Never invents a time. */
function relativeTime(createdAt: string, now: number = Date.now()): string {
  const then = Date.parse(createdAt);
  if (!createdAt || Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString();
}

/**
 * Maps one raw gateway row to the shared row view model, or `null` when the
 * row cannot be addressed: a row with no id has no
 * `POST /v1/notifications/{id}/read`, so rendering it would put an
 * un-actionable row in the list (and an empty id in the read route).
 */
function mapApiNotificationItem(raw: Record<string, unknown>): Notification | null {
  const id = String(raw.id ?? "");
  if (!id) return null;
  const type = String(raw.type ?? raw.eventType ?? raw.event ?? "");
  const message = String(raw.message ?? raw.body ?? raw.detail ?? "");
  const title = String(raw.title ?? "").trim() || humaniseEventType(type) || "Notification";
  return {
    id,
    title,
    message,
    timeAgo: relativeTime(String(raw.createdAt ?? raw.created_at ?? "")),
    read: Boolean(raw.read),
    kind: kindFromEventType(type),
    href: hrefFromEvent(type, String(raw.subjectRef ?? "")),
  };
}

const DISABLED_RESULT: UseNotificationsResult = {
  notifications: [],
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  source: "api",
  refresh: () => {},
  canMarkRead: false,
  markRead: async () => {},
  markAllRead: async () => {},
};

/**
 * Owns one notification feed. `enabled` is `false` only when a
 * `NotificationsProvider` already owns the feed for this subtree: the inert
 * instance never fetches and never contributes rows, it exists so
 * `useNotifications()` can call its hooks unconditionally (a conditional hook
 * call would break the hook order the moment the provider mounts).
 *
 * Backs the topbar bell and the `/notifications` page with the same
 * `fetchNotifications` / `markNotificationRead` calls the topbar has always
 * used (ALL-4747 G6) — no new endpoint, no fixture. The `/home` notifications
 * tile keeps consuming `notifications`; the notification center consumes
 * `items`, which is the same payload mapped to the shared row view model.
 */
export function useNotificationsSource(enabled = true): UseNotificationsResult {
  const { allowed: canMarkRead } = useLoadedPermission(NOTIFICATION_WRITE_ACTION.permission, NOTIFICATION_WRITE_ACTION.scope);
  // Raw rows are the single source of truth; both views are derived from them,
  // so a read mark (or a rollback) can never make the two views disagree.
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchNotifications();
      if (result.reasonCode) {
        setRows([]);
        setError(result.reasonCode);
      } else if (!result.notifications.every((r) => r && typeof r === "object")) {
        // A malformed row (the gateway is not contracted to send one) must
        // degrade to an error, never to a partial list or a render crash.
        setRows([]);
        setError("MALFORMED_NOTIFICATION_RESPONSE");
      } else {
        setRows(result.notifications);
      }
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { if (enabled) void load(); }, [enabled, load]);

  const markRead = useCallback(async (id: string) => {
    if (!enabled || !notificationWriteAllowed()) return;
    const existing = rows.find((r) => String(r.id ?? "") === id);
    // A row that is already read needs no write: the POST would be a no-op on
    // the server and would only risk flipping the row to unread on a failure.
    if (existing && Boolean(existing.read)) return;
    // Mark optimistically, then roll back per id below. Both updates are
    // functional so a concurrent change to *another* row — a second read in
    // flight, a refresh — is never reverted by this call's rollback.
    setRows((cur) => cur.map((r) => (String(r.id ?? "") === id ? { ...r, read: true } : r)));
    const result = await markNotificationRead(id);
    if (!result.ok) {
      // Withdraw only this row's claim rather than restoring a whole-array
      // snapshot taken before the write.
      setRows((cur) => cur.map((r) => (String(r.id ?? "") === id ? { ...r, read: false } : r)));
    }
  }, [enabled, rows]);

  const markAllRead = useCallback(async () => {
    if (!enabled || !notificationWriteAllowed()) return;
    const unreadIds = rows
      .filter((r) => !Boolean(r.read))
      .map((r) => String(r.id ?? ""))
      .filter(Boolean);
    if (unreadIds.length === 0) return;
    setRows((cur) => cur.map((r) => ({ ...r, read: true })));
    const results = await Promise.all(unreadIds.map((id) => markNotificationRead(id)));
    if (results.some((r) => !r.ok)) {
      // A partial failure leaves the list claiming reads the server rejected:
      // re-read the server's truth instead of showing the optimistic state.
      await load();
    }
  }, [enabled, rows, load]);

  const notifications = useMemo(() => rows.map(mapApiNotification), [rows]);
  const items = useMemo(
    () => rows.map(mapApiNotificationItem).filter((n): n is Notification => n !== null),
    [rows],
  );
  const unreadCount = notifications.filter((n) => !n.read).length;

  return useMemo(
    () => (enabled
      ? { notifications, items, unreadCount, loading, error, source: "api", refresh: load, canMarkRead, markRead, markAllRead }
      : DISABLED_RESULT),
    [enabled, notifications, items, unreadCount, loading, error, load, canMarkRead, markRead, markAllRead],
  );
}

/**
 * The notification feed for any surface that shows one.
 *
 * When a `NotificationsProvider` wraps this consumer the provider's feed is
 * returned, so every surface below it reads and mutates one set of rows; with
 * no provider this instance owns a private feed. Both paths are the same
 * implementation — the private one is simply inert while a provider is present.
 */
export function useNotifications(): UseNotificationsResult {
  const shared = useContext(NotificationsContext);
  const local = useNotificationsSource(shared === null);
  return shared ?? local;
}
